import { useMemo } from 'react';
import {
  Box,
  ColumnLayout,
  Container,
  Header,
  KeyValuePairs,
  StatusIndicator,
} from '@amzn/awsui-components-console';
import type { AuditEntry } from '@amzn/helianthus-arbiter-client';
import {
  computeAuditTrailMetrics,
  formatDuration,
  formatTokenCount,
} from '../utils/auditTrailMetrics';

interface PerformanceMetricsPanelProps {
  entries: AuditEntry[];
}

export function PerformanceMetricsPanel({ entries }: PerformanceMetricsPanelProps): React.JSX.Element {
  const metrics = useMemo(() => computeAuditTrailMetrics(entries), [entries]);
  const hasLlm = metrics.llmCallCount > 0;
  const naValue = <Box color="text-status-inactive">{'\u2014'}</Box>;

  return (
    <Container header={<Header variant="h2">Performance Metrics</Header>}>
      <ColumnLayout columns={3} variant="text-grid">
        <KeyValuePairs
          columns={1}
          items={[
            {
              label: 'Total Duration',
              value: formatDuration(metrics.totalDurationMs),
            },
            {
              label: 'LLM Calls',
              value: hasLlm ? String(metrics.llmCallCount) : naValue,
            },
          ]}
        />
        <KeyValuePairs
          columns={1}
          items={[
            {
              label: 'Input Tokens',
              value: hasLlm ? formatTokenCount(metrics.totalInputTokens) : naValue,
            },
            {
              label: 'Output Tokens',
              value: hasLlm ? formatTokenCount(metrics.totalOutputTokens) : naValue,
            },
          ]}
        />
        <KeyValuePairs
          columns={1}
          items={[
            {
              label: 'Rules Evaluated',
              value: String(metrics.totalRuleCount),
            },
            {
              label: 'Outcomes',
              value: metrics.totalRuleCount > 0 ? (
                <Box>
                  <StatusIndicator type="success">{metrics.rulesPassCount} passed</StatusIndicator>
                  {metrics.rulesFailCount > 0 && (
                    <Box margin={{ left: 's' }} display="inline">
                      <StatusIndicator type="error">{metrics.rulesFailCount} failed</StatusIndicator>
                    </Box>
                  )}
                  {metrics.rulesErrorCount > 0 && (
                    <Box margin={{ left: 's' }} display="inline">
                      <StatusIndicator type="warning">{metrics.rulesErrorCount} error</StatusIndicator>
                    </Box>
                  )}
                </Box>
              ) : (
                <Box color="text-status-inactive">--</Box>
              ),
            },
          ]}
        />
      </ColumnLayout>
    </Container>
  );
}
